import { push } from "redux-first-history";
import * as monitoringApi from "api/endpoints/monitoring";
import * as umApi from "api/endpoints/userMangement";
import { setupTokenInterceptor, setBaseUrl } from "api/interceptors";
import * as authApi from "api/endpoints/auth";
import * as usersApi from "api/endpoints/users";
import sidebarData from "../../data/sidebar-data.json";
import getBaseUrl from "utils/getBaseUrl";
import { createSlice, createSliceSelector } from "../factory";

const name = "app";

const previousState = {
  production: null,
  sandbox: null
};

function getInitialState(environment) {
  const state = previousState[environment];

  if (state) return state;
  return {
    loading: false,
    isPageLoading: false,
    environment: "production",
    userData: null,
    permissions: {},
    portfolios: [],
    partners: [],
    selectedPartnerId: Number(localStorage.getItem("selectedPartnerId")) || null,
    isPartnerChanged: true
  };
}

const initialState = getInitialState();

const slice = createSlice(name, initialState);

export default slice.reducer;

/* Selectors */
const createSelector = createSliceSelector(name);

export const selectApp = createSelector((state) => {
  return state;
});

export const selectEnvironment = createSelector((state) => {
  return state.environment;
});

export const selectLoading = createSelector((state) => {
  return state.loading;
});

export const selectIsPageLoading = createSelector((state) => {
  return state.isPageLoading;
});

export const selectPortfolios = createSelector((state) => {
  return state.portfolios;
});

const selectSelectedPartner = createSelector(selectEnvironment, (state, environment) => {
  if (!state.partners.length) return;

  const partner = state.partners.find((p) => p.id === state.selectedPartnerId) || state.partners[0];

  return {
    ...partner,
    stagingEpId: undefined,
    id: environment === "production" ? partner.id : partner.stagingEpId
  };
});

export const selectPartnersData = createSelector(selectSelectedPartner, (state, selectedPartner) => {
  return {
    partners: state.partners,
    selectedPartner: selectedPartner,
    isPartnerChanged: state.isPartnerChanged
  };
});

export const selectUser = createSelector(selectSelectedPartner, (state, selectedPartner) => {
  if (state.userData === null) return null;

  const userType = state.userData.details.epUserAttributes?.userType;

  return {
    type: userType,
    isAdmin: userType === "A",
    isDeveloper: userType === "D",
    environment: state.environment,
    name: state.userData.details.epUserAttributes.name,
    role: state.userData.details.accessControlRole,
    isSuperUser: state.userData.details.isSuperUser,
    epId: state.userData.details.epInfo?.id,
    userAccessRole: state.userData.details.userAccessRole,
    //For super admin, assign the the partner from the dropdown. for any other user, assign current user info
    selectedPartner: state.selectedPartnerId
      ? selectedPartner
      : state.userData.details.epInfo || {
          id: null,
          name: "",
          description: "",
          status: "",
          logoURI: ""
        }
  };
});

export const selectMenu = createSelector((state) => {
  const data = JSON.parse(JSON.stringify(sidebarData));
  prepareMenu(data, state.permissions);
  return data;
});

function prepareMenu(nodes, permissions) {
  nodes.forEach((node) => {
    let visible = true;
    if (permissions[node.id] !== undefined) {
      visible = permissions[node.id];
    }

    node.visible = visible;

    if (node.items) {
      prepareMenu(node.items, permissions);

      if (node.items.filter((ni) => ni.visible).length === 0) {
        node.visible = false;
      }
    }
  });
}

/* Actions */
const { update } = slice.actions;

export function loadCurrentSession(currentPath) {
  return async function (dispatch, getState) {
    try {
      let environment = selectEnvironment(getState());
      dispatch(update({ loading: true }));
      const awsUserInfo = await authApi.currentUserInfo();

      if (!awsUserInfo) {
        dispatch(push(`/sign-in?redirectUrl=${currentPath}`));
        return;
      }

      if (awsUserInfo?.attributes?.["custom:userType"] === "D") {
        environment = "sandbox";
      }

      setupTokenInterceptor();
      // setupUnauthorizedInterceptor();

      const payload = {
        ...getInitialState()
      };

      dispatch(update(payload));
      dispatch(loadEnvironmentData(environment));

      dispatch(loadPartners());
    } catch (e) {
      dispatch(push(`/sign-in?redirectUrl=${currentPath}`));
    }
  };
}

async function fetchEnvironmentData() {
  const userPromise = usersApi.get().catch(handleError);
  const permissionPromise = usersApi.getPermissions().catch(handleError);

  function handleError() {
    return null;
  }

  return Promise.all([userPromise, permissionPromise]).then((values) => {
    return {
      userData: values[0],
      permissionData: values[1]
    };
  });
}

export function loadUserData() {
  return async function (dispatch) {
    const data = await usersApi.get();
    const payload = { userData: data };
    dispatch(update(payload));
  };
}

export function loadingPage(value) {
  return async function (dispatch) {
    const payload = { isPageLoading: value };
    dispatch(update(payload));
  };
}

export function loadEnvironmentData(environment) {
  return async function (dispatch) {
    setBaseUrl(getBaseUrl(environment));

    const data = await fetchEnvironmentData();

    const payload = { environment, userData: data.userData, permissions: data.permissionData.details };

    dispatch(update(payload));
  };
}

export function loadPortfolios(epId) {
  const config = { params: { epId: epId } };
  return async function (dispatch) {
    let data = await monitoringApi.getPortfolios(config);
    data = data.details.map((p) => {
      return {
        id: p.partnerPortfoliosId,
        label: p.name
      };
    });

    const payload = { portfolios: data };

    dispatch(update(payload));
  };
}

export function resetApp() {
  return async function (dispatch) {
    const payload = getInitialState();
    localStorage.clear();
    dispatch(update(payload));
  };
}

export function setEnvironment(environment) {
  return async function (dispatch, getState) {
    await dispatch(loadEnvironmentData(environment));

    if (environment === "sandbox") {
      dispatch(push("/system-level-settings/access-administration"));
    } else {
      dispatch(push("/business-lookup"));
    }
  };
}

export function loadPartners() {
  return async function (dispatch, getState) {
    const partnersData = await umApi.getPartners();
    const payload = { partners: partnersData.details || [] };

    dispatch(update(payload));
  };
}

export function setSelectedPartner(id) {
  return async function (dispatch) {
    localStorage.setItem("selectedPartnerId", id);
    const payload = { selectedPartnerId: id };
    dispatch(update(payload));
  };
}
